/**
 * XMLUI Header Component
 * 
 * This component provides the main header with:
 * - Money Mojo branding
 * - Region-aware utility area (currency/date display)
 * - Theme toggle
 * - User profile area (placeholder)
 * - Skip-to-content link target
 * 
 * Features:
 * - Accessibility compliance (WCAG 2.2 AA)
 * - Responsive design
 * - Region Pack integration
 * - Theme switching
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabaseAuthService } from '../../services/supabase-service';
import { profileService } from '../../services/profile-service';
import { useNavigationGuardState } from '../../pages/settings/useNavigationGuardState';
import {
  useNotifications,
  useNotificationCount,
  useMarkNotificationRead,
  useDismissNotification,
} from '../../hooks/useBudgetOverview';
import { 
  Sun, 
  Moon, 
  User, 
  Bell, 
  Settings,
  FolderTree,
  Bot,
  Shield,
  Zap,
  LogOut,
  X,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useRegion } from '../../contexts/RegionContext';
import { usePreferences } from '../../hooks/usePreferences';
import { useMobileMenu } from '../../contexts/MobileMenuContext';
import { useMojoAgent } from '../../contexts/MojoAgentContext';
import { useDemoMode } from '../../hooks/useDemoMode';
import type { UserPreferences } from '../../types/preference.types';
import { formatTime } from '../../lib/regions/au';
import { Avatar } from '../ui/Avatar';
import type { Profile } from '../../types/profile.types';

// Currency symbols mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: '$',
  AUD: '$',
};

/**
 * Header Component
 * 
 * Provides the main application header with branding, utilities, and navigation.
 */
export function Header(): React.ReactElement {
  const { theme, toggleTheme } = useTheme();
  const { regionPack, formatDate } = useRegion();
  const { data: preferences } = usePreferences();
  const { isDemoMode } = useDemoMode();
  // Type assertion to handle undefined case - preferences can be UserPreferences | undefined
  const userPreferences: UserPreferences | undefined = preferences ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { hasUnsavedChanges, onNavigationAttempt } = useNavigationGuardState();
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();
  const { isOpen: isMojoOpen, toggle: toggleMojo, registerToggleRef } = useMojoAgent();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationMenuRef = useRef<HTMLDivElement>(null);

  const { data: notifications = [] } = useNotifications();
  const { data: unreadCount = 0 } = useNotificationCount();
  const markRead = useMarkNotificationRead();
  const dismissNotification = useDismissNotification();

  const handleMojoToggle = () => {
    setIsNotificationMenuOpen(false);
    setIsProfileMenuOpen(false);
    toggleMojo();
  };

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const result = await profileService.getProfile();
        if (result.data) {
          setProfile(result.data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, []);

  // Generate initials from first and last name
  const getInitials = (firstName?: string, lastName?: string): string => {
    const first = firstName?.charAt(0).toUpperCase() || '';
    const last = lastName?.charAt(0).toUpperCase() || '';
    return first + last || 'U'; // Fallback to 'U' if no name
  };

  const displayName = profile?.firstName || 'User';
   
  void getInitials(profile?.firstName, profile?.lastName);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (notificationMenuRef.current && !notificationMenuRef.current.contains(event.target as Node)) {
        setIsNotificationMenuOpen(false);
      }
    };

    if (isProfileMenuOpen || isNotificationMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProfileMenuOpen, isNotificationMenuOpen]);

  // Close menus on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isProfileMenuOpen) setIsProfileMenuOpen(false);
        if (isNotificationMenuOpen) setIsNotificationMenuOpen(false);
      }
    };

    if (isProfileMenuOpen || isNotificationMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isProfileMenuOpen, isNotificationMenuOpen]);

  const isDarkMode = theme === 'dark';

  const profileMenuItems = [
    { id: 'profile', label: 'Profile', icon: User, path: '/settings/profile' },
    { id: 'account', label: 'Account Settings', icon: Settings, path: '/settings/account' },
    { id: 'categorisation', label: 'Categorisation', icon: FolderTree, path: '/settings/categorisation' },
    { id: 'ai', label: 'AI Configuration', icon: Bot, path: '/settings/ai' },
    { id: 'security', label: 'Security & Privacy', icon: Shield, path: '/settings/security' },
    { id: 'notifications', label: 'Notifications', icon: Bell, path: '/settings/notifications' },
    { id: 'rules', label: 'Rules', icon: Zap, path: '/settings/rules' },
    { id: 'signout', label: 'Sign Out', icon: LogOut, path: '/logout', isDestructive: true },
  ];

  const handleProfileMenuClick = async (path: string) => {
    if (path === '/logout') {
      // Handle logout - bypass navigation guard for logout
      try {
        await supabaseAuthService.signOut();
        // Clear any local storage or session data
        localStorage.removeItem('moneymojo-theme');
        // Redirect to login page
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Error signing out:', error);
        // Still redirect to login even if signout fails
        navigate('/login', { replace: true });
      }
      setIsProfileMenuOpen(false);
      return;
    }

    // Check if navigating away from current page
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    console.log('[Header] Profile menu click:', { path, hasUnsavedChanges, isActive, currentPath: location.pathname });

    // Check for unsaved changes before navigating
    if (hasUnsavedChanges && !isActive) {
      onNavigationAttempt(path);
      // Keep menu open - dialog will handle closing it
    } else {
      console.log('[Header] No unsaved changes or already active, navigating directly');
      navigate(path);
      setIsProfileMenuOpen(false);
    }
  };

  return (
    <header 
      className="sticky top-0 z-[60] w-full min-w-0 max-w-full border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:border-gray-700 dark:bg-gray-900/95 supports-[backdrop-filter]:dark:bg-gray-900/60"
      role="banner"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo and Brand */}
          <div className="flex items-center">
            <a 
              href="/dashboard" 
              className="flex items-center space-x-2 focus-ring rounded-md p-2"
              aria-label="Money Mojo - Go to Dashboard"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-blue-600 flex-shrink-0">
                <img 
                  src="/logo.png" 
                  alt="Money Mojo" 
                  className="h-6 w-6 object-contain"
                />
              </div>
              <span className="hidden text-xl font-bold text-gray-900 dark:text-gray-100 font-brand tracking-tight sm:block header-brand-text">
                Money Mojo
              </span>
            </a>
          </div>

          {/* Region-aware Utility Area - Icons visible on all sizes */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Currency and Date Display - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              <span>{formatDate(currentTime)}</span>
              <span>{formatTime(currentTime, { use24Hour: userPreferences?.timeFormat === '24h' })}</span>
              <span>
                {userPreferences?.currency || regionPack.currency.code}{' '}
                {userPreferences?.currencyDisplay === 'code' 
                  ? userPreferences.currency 
                  : (userPreferences?.currency ? CURRENCY_SYMBOLS[userPreferences.currency] : regionPack.currency.symbol)}
              </span>
            </div>

            {/* Theme Toggle - Visible on all sizes */}
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100"
              aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
              title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
              data-testid="theme-toggle"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            {/* Notifications - Visible on all sizes */}
            <div className="w-10 md:w-12 flex justify-center relative" ref={notificationMenuRef}>
              <button
                onClick={() => {
                  setIsNotificationMenuOpen(!isNotificationMenuOpen);
                  setIsProfileMenuOpen(false);
                }}
                className="relative p-2 text-gray-600 hover:text-gray-900 bg-gray-100 rounded-full dark:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
                aria-label={`View notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                title="View notifications"
                data-testid="notification-badge"
                aria-expanded={isNotificationMenuOpen}
                aria-haspopup="menu"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {isNotificationMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[100]"
                  role="menu"
                  aria-label="Notifications"
                >
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                    <button
                      type="button"
                      onClick={() => setIsNotificationMenuOpen(false)}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded"
                      aria-label="Close notifications"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {notifications.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No notifications
                    </p>
                  ) : (
                    <div className="py-1">
                      {notifications.map((notification) => {
                        const isUnread = !notification.readAt;
                        const severityIcon =
                          notification.severity === 'critical' || notification.severity === 'warning' ? (
                            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                          );
                        return (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                              isUnread ? 'bg-purple-50/50 dark:bg-purple-900/10' : ''
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              {severityIcon}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {notification.message}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  {isUnread && (
                                    <button
                                      type="button"
                                      onClick={() => markRead.mutate(notification.id)}
                                      className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                                    >
                                      Mark read
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => dismissNotification.mutate(notification.id)}
                                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                                  >
                                    Dismiss
                                  </button>
                                  {notification.type === 'bill_reminder' && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setIsNotificationMenuOpen(false);
                                        navigate('/budgets');
                                      }}
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      View bills
                                    </button>
                                  )}
                                  {notification.type === 'budget_alert' && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setIsNotificationMenuOpen(false);
                                        navigate('/budgets');
                                      }}
                                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                      View budgets
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mojo Agent */}
            <button
              ref={registerToggleRef}
              type="button"
              onClick={handleMojoToggle}
              data-testid="mojo-agent-toggle"
              aria-label={isMojoOpen ? 'Close Mojo Agent' : 'Open Mojo Agent'}
              aria-expanded={isMojoOpen}
              aria-controls="mojo-agent-panel"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium focus-ring transition-colors ${
                isMojoOpen
                  ? 'bg-purple-700 text-white ring-2 ring-purple-400 ring-offset-2 dark:ring-offset-gray-900'
                  : 'bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700'
              }`}
            >
              <Bot className="h-4 w-4" aria-hidden />
              <span>Mojo</span>
            </button>

            {/* Demo Badge */}
            {isDemoMode && (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-[#FAEBD7] text-[#A52A2A] dark:bg-orange-900/30 dark:text-orange-300">
                Demo
              </span>
            )}

            {/* User Profile - Visible on all sizes */}
            <div className="w-auto md:w-32 lg:w-40 xl:w-40 flex-shrink-0 relative" ref={profileMenuRef}>
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                className={`flex items-center space-x-2 p-2 rounded-md w-full transition-all overflow-hidden ${
                  isProfileMenuOpen
                    ? 'ring-2 ring-blue-500 ring-offset-2 text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
                aria-label="Open user profile menu"
                aria-expanded={isProfileMenuOpen}
                aria-haspopup="menu"
                data-testid="profile-menu-trigger"
              >
                <Avatar 
                  profile={profile || undefined} 
                  size="sm" 
                  className="flex-shrink-0"
                />
                <span 
                  data-testid="user-name" 
                  className="text-sm font-medium hidden lg:block min-w-0 truncate"
                  title={displayName}
                >
                  {displayName}
                </span>
                <svg 
                  className={`h-4 w-4 flex-shrink-0 hidden md:block transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              {/* Profile Dropdown Menu */}
              {isProfileMenuOpen && (
                <div 
                  className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[100]"
                  role="menu"
                  aria-label="User profile menu"
                >
                  <div className="py-2">
                    {profileMenuItems.map((item, index) => {
                      const Icon = item.icon;
                      const showDivider = index === profileMenuItems.length - 1; // Before Sign Out
                      
                      return (
                        <React.Fragment key={item.id}>
                          {showDivider && (
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                          )}
                          <button
                            onClick={() => handleProfileMenuClick(item.path)}
                            className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${
                              item.isDestructive
                                ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                            role="menuitem"
                            aria-label={item.label}
                          >
                            <Icon className="w-5 h-5 flex-shrink-0" />
                            <span>{item.label}</span>
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle (Hamburger) - Visible on mobile only, to the right of profile */}
            <div className="w-10 md:w-12 flex justify-center md:hidden">
              <button 
                data-testid="mobile-menu-toggle"
                className={`p-2 rounded-full transition-all duration-300 ease-in-out ${
                  isMobileMenuOpen
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-100'
                }`}
                aria-label="Toggle mobile menu"
                aria-expanded={isMobileMenuOpen}
                onClick={toggleMobileMenu}
              >
                <div className="relative w-5 h-5 flex items-center justify-center">
                  <span className={`absolute block h-0.5 w-5 bg-current transition-all duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-1.5'
                  }`} />
                  <span className={`absolute block h-0.5 w-5 bg-current transition-all duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                  }`} />
                  <span className={`absolute block h-0.5 w-5 bg-current transition-all duration-300 ease-in-out ${
                    isMobileMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-1.5'
                  }`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200 dark:border-gray-700 md:hidden">
            <div className="flex flex-col space-y-2 py-4">
              {/* Mobile Region Info - Right aligned with date | currency format */}
              <div className="flex items-center justify-end px-2 py-2 text-sm text-gray-600 dark:text-gray-400">
                <span>
                  {formatDate(currentTime)} {formatTime(currentTime, { use24Hour: userPreferences?.timeFormat === '24h' })} | {userPreferences?.currency || regionPack.currency.code}{' '}
                  {userPreferences?.currencyDisplay === 'code' 
                    ? userPreferences.currency 
                    : (userPreferences?.currency ? CURRENCY_SYMBOLS[userPreferences.currency] : regionPack.currency.symbol)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
